import React, { useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";

import IconButton from "../IconButton";
import { ArrowLeft, ArrowRight } from "../Icons";

import styles from "./EntityNavigation.module.scss";

const NEAR_END_THRESHOLD = 5;

interface Props {
  idPrefix: string;
  numbers: string[];
  total: number;
  currentNumber: string;
  buildUrl: (entityNumber: string) => string;
  formatPosition: (position: number, total: number) => string;
  previousLabel: string;
  nextLabel: string;
  onNeedMore?: () => void;
}

const EntityNavigation = (props: Props) => {
  const {
    idPrefix,
    numbers,
    total,
    currentNumber,
    buildUrl,
    formatPosition,
    previousLabel,
    nextLabel,
    onNeedMore,
  } = props;

  const history = useHistory();
  const index = useMemo(() => numbers.indexOf(currentNumber), [
    numbers,
    currentNumber,
  ]);
  const requestedAtLength = useRef<number | null>(null);

  // -1 means the record sits on a page the caller has not loaded yet, so more pages are needed
  // to place it at all - not just to step past the end of what is loaded.
  const needsMore =
    index === -1 || numbers.length - index <= NEAR_END_THRESHOLD;

  useEffect(() => {
    if (!onNeedMore || !needsMore) {
      return;
    }

    // Callers pass an inline closure, so a new identity arrives on every parent render while
    // needsMore stays true. Firing once per loaded length is what stops that becoming a loop.
    if (requestedAtLength.current === numbers.length) {
      return;
    }

    requestedAtLength.current = numbers.length;
    onNeedMore();
  }, [needsMore, numbers.length, onNeedMore]);

  if (index === -1 || numbers.length < 2) {
    return null;
  }

  return (
    <div className={styles.entityNav}>
      <IconButton
        id={`${idPrefix}-previous-button`}
        aria-label={previousLabel}
        disabled={index === 0}
        className={styles.entityNavButton}
        onClick={() => history.push(buildUrl(numbers[index - 1]))}
      >
        <ArrowLeft fillColor="currentColor" />
      </IconButton>
      <span className={styles.entityNavPosition}>
        {formatPosition(index + 1, total)}
      </span>
      <IconButton
        id={`${idPrefix}-next-button`}
        aria-label={nextLabel}
        disabled={index === numbers.length - 1}
        className={styles.entityNavButton}
        onClick={() => history.push(buildUrl(numbers[index + 1]))}
      >
        <ArrowRight fillColor="currentColor" />
      </IconButton>
    </div>
  );
};

export default EntityNavigation;
